import { ChangeEvent, useCallback, useEffect, useRef } from 'react';
import { messages } from '../../modules/bank-statements/bank-statements-messages';
import { ArrowIcon, UndoIcon } from '../icons';
import { theme } from '@algoan/dashboard/theme';
import { addAlpha } from '@algoan/deep-dive/theme';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

interface NotesRowProps {
  handleInputOnChange: (event: ChangeEvent<HTMLTextAreaElement>) => void;
  index: number;
  textInputValue: string;
  setTextInputValue: (value: string) => void;
  width?: number;
  handleSubmit: () => void;
  maximumNoteCharacterLength: number;
  savedNoteValue?: string;
  textHasChanged: boolean;
  isMultiline?: boolean;
  isRecategorization?: boolean;
}

export const NotesRow: React.FC<NotesRowProps> = ({
  handleInputOnChange,
  index,
  textInputValue,
  setTextInputValue,
  width,
  handleSubmit,
  textHasChanged,
  maximumNoteCharacterLength,
  savedNoteValue,
  isMultiline,
  isRecategorization,
}) => {
  const intl = useIntl();

  const inputRef = useRef<HTMLTextAreaElement>(null);

  const beforeUnload = useCallback(
    (event: BeforeUnloadEvent): string => {
      event.preventDefault();
      event.returnValue = intl.formatMessage(messages.unloadMessage);
      return event.returnValue;
    },
    [intl],
  );

  useEffect(() => {
    inputRef.current?.focus({
      preventScroll: true,
    });
  }, [inputRef]);

  useEffect(() => {
    if (textHasChanged) {
      window.addEventListener('beforeunload', beforeUnload);
    } else {
      window.removeEventListener('beforeunload', beforeUnload);
    }

    return () => {
      window.removeEventListener('beforeunload', beforeUnload);
    };
  }, [textHasChanged, beforeUnload]);

  return (
    <S.Container index={index}>
      <S.NotesInput
        data-testid="notes-input"
        onChange={handleInputOnChange}
        inputColor={index % 2 === 0 ? theme.secondary.chalk : theme.white}
        value={textInputValue}
        placeholder={intl.formatMessage(messages.notesPlaceholder)}
        width={width}
        ref={inputRef}
        onKeyDown={(event) => (event.key === 'Enter' ? handleSubmit() : undefined)}
        height={isMultiline ? '47px' : '32px'}
        maxLength={maximumNoteCharacterLength}
        isRecategorization={isRecategorization}
      />
      <S.ButtonContainer>
        <S.UndoButton
          onClick={() => setTextInputValue(savedNoteValue ?? '')}
          isHidden={!textHasChanged}
          isRecategorisation={isRecategorization}
        >
          <S.StyledUndoIcon />
        </S.UndoButton>
        <S.Divider />
        <S.SubmitButton onClick={handleSubmit}>
          <S.StyledArrowIcon color={theme.secondary.softBlue1} direction={'up'} />
        </S.SubmitButton>
      </S.ButtonContainer>
    </S.Container>
  );
};

const S = {
  Container: styled.div<{ index: number }>`
    background-color: ${(props) => (props.index % 2 === 0 ? props.theme.secondary.chalk : props.theme.white)};
    display: flex;
    flex-direction: row;
    width: 100%;
    border-top: 1px solid ${({ theme }) => addAlpha(theme.algoanBlue, 0.05)};
  `,
  NotesInput: styled.textarea<{
    isRecategorization?: boolean;
    width?: number;
    inputColor?: string;
    height: CSSStyleDeclaration['height'];
  }>`
    ${(props) => (props.isRecategorization ? 'width: 663px' : 'width: 954px')};
    width: calc(${(props) => props.width}px - 78px);
    border: none;
    max-height: ${(props) => props.height};
    height: ${(props) => props.height};
    padding: 5px 10px 10px;
    background-color: ${(props) => props.inputColor};
    color: ${(props) => props.theme.algoanBlue};
    line-height: 20px;
    resize: none;
    font-size: 14px;

    &:focus {
      outline: none;
    }

    &::placeholder {
      color: ${(props) => props.theme.secondary.softBlue1};
      font-weight: 400;
      font-size: 13px;
    }
  `,
  Divider: styled.div`
    border-right: solid 1px ${({ theme }) => addAlpha(theme.algoanBlue, 0.05)};
    height: 47px;
    margin: auto;
    margin-left: 2px;
    z-index: 5;
  `,
  SubmitButton: styled.div`
    width: 33px;
    margin-left: -1px;
    height: 35px;
    position: relative;
    cursor: pointer;
  `,
  StyledArrowIcon: styled(ArrowIcon)`
    height: 10px;
    width: 25px;
    position: absolute;
    top: 11px;

    &:hover {
      & path {
        stroke: ${(props) => props.theme.secondary.softBlue2};
      }
    }
  `,
  ButtonContainer: styled.div`
    width: 51px;
    display: flex;
  `,
  UndoButton: styled.div<{ isHidden?: boolean; isRecategorisation?: boolean }>`
    width: ${({ isRecategorisation }) => (isRecategorisation ? '28px' : '32px')};
    visibility: ${(props) => (props.isHidden ? 'hidden;' : '')};
    z-index: 10;
    height: 42px;
    background-color: rgba(0, 0, 0, 0);
    cursor: pointer;

    &:hover {
      & path {
        fill: ${(props) => props.theme.secondary.softBlue2};
      }
    }
  `,
  StyledUndoIcon: styled(UndoIcon)`
    margin-left: 4px;
    margin-top: 12px;
  `,
};
